/**
 * @fileoverview React hook for X/Twitter Connect operations.
 * @module x-twitter-connect/hooks/useXTwitterConnect
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createXTwitterConnectService } from '../services/XTwitterConnectService';
import type { XTwitterConnectionStatus, XTwitterSyncOwnPostsResponse } from '../types';

const OAUTH_SESSION_KEY = 'x_twitter_oauth_in_progress';

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

export interface UseXTwitterConnectResult {
  status: XTwitterConnectionStatus | null;
  isConnected: boolean;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  syncPreview: XTwitterSyncOwnPostsResponse | null;
  refreshStatus: () => Promise<void>;
  previewOwnPostsSync: () => Promise<void>;
  syncOwnPosts: () => Promise<void>;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
}

export function useXTwitterConnect(projectId: string): UseXTwitterConnectResult {
  const [status, setStatus] = useState<XTwitterConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncPreview, setSyncPreview] = useState<XTwitterSyncOwnPostsResponse | null>(null);
  const statusAbortRef = useRef<AbortController | null>(null);
  const processedCallbackRef = useRef(false);
  const mountedRef = useRef(true);

  const service = useMemo(() => createXTwitterConnectService(), []);

  const refreshStatus = useCallback(async () => {
    if (!projectId) return;

    statusAbortRef.current?.abort();
    statusAbortRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);
      const nextStatus = await service.getStatus(projectId, statusAbortRef.current.signal);
      if (mountedRef.current) {
        setStatus(nextStatus);
      }
    } catch (err) {
      if (isAbortError(err)) return;
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load X connection status');
        setStatus({ connected: false });
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [projectId, service]);

  const connect = useCallback(async () => {
    if (!projectId) return;

    try {
      setActionLoading(true);
      setError(null);
      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const oauthUrl = await service.getOAuthUrl(projectId, returnTo);
      sessionStorage.setItem(OAUTH_SESSION_KEY, projectId);
      window.location.href = oauthUrl;
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to connect X account');
        setActionLoading(false);
      }
    }
  }, [projectId, service]);

  const disconnect = useCallback(async () => {
    if (!projectId) return;

    try {
      setActionLoading(true);
      setError(null);
      const nextStatus = await service.disconnect(projectId);
      if (mountedRef.current) {
        setStatus(nextStatus);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to disconnect X account');
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setActionLoading(false);
      }
    }
  }, [projectId, service]);

  const previewOwnPostsSync = useCallback(async () => {
    if (!projectId) return;
    try {
      setActionLoading(true);
      setError(null);
      setSyncPreview(await service.syncOwnPosts({
        projectId,
        maxPages: 1,
        limit: 100,
        maxCostUsd: 1,
        dryRun: true,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to preview X sync');
    } finally {
      if (mountedRef.current) setActionLoading(false);
    }
  }, [projectId, service]);

  const syncOwnPosts = useCallback(async () => {
    if (!projectId) return;
    try {
      setActionLoading(true);
      setError(null);
      await service.syncOwnPosts({ projectId, maxPages: 1, limit: 100, maxCostUsd: 1 });
      setSyncPreview(null);
      await refreshStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync X posts');
    } finally {
      if (mountedRef.current) setActionLoading(false);
    }
  }, [projectId, refreshStatus, service]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      statusAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    if (processedCallbackRef.current || !projectId) return;

    const params = new URLSearchParams(window.location.search);
    const callbackState = params.get('xTwitter');
    const callbackError = params.get('error');
    const oauthProjectId = sessionStorage.getItem(OAUTH_SESSION_KEY);

    if (!callbackState) return;
    if (oauthProjectId && oauthProjectId !== projectId) return;

    if (callbackState === 'connected') {
      processedCallbackRef.current = true;
      sessionStorage.removeItem(OAUTH_SESSION_KEY);
      window.history.replaceState({}, '', window.location.pathname);
      refreshStatus();
      return;
    }

    if (callbackState === 'error' || callbackError) {
      processedCallbackRef.current = true;
      sessionStorage.removeItem(OAUTH_SESSION_KEY);
      window.history.replaceState({}, '', window.location.pathname);
      setError(callbackError ? decodeURIComponent(callbackError) : 'X OAuth failed');
    }
  }, [projectId, refreshStatus]);

  return {
    status,
    isConnected: status?.connected ?? false,
    loading,
    actionLoading,
    error,
    syncPreview,
    refreshStatus,
    previewOwnPostsSync,
    syncOwnPosts,
    connect,
    disconnect,
  };
}

export default useXTwitterConnect;
